import React, { useEffect, useMemo, useState } from "react";
import { Edit3, X, Loader2, Save, ChevronDown, Sparkles, Upload, BookPlus } from "lucide-react";
import { toast } from "sonner";

import { api } from "../lib/api";
import { gbp } from "../lib/format";
import {
  computeRecipeTotals,
  synthesiseSnapshotArrays,
  applyRecipeArraysToLine,
  entryCostPerUnit,
  resolveEntryWastage,
  WASTAGE_SOURCE_LABEL,
} from "../lib/recipe";
import { PrimaryButton, QuietButton, Modal } from "./UI";
import { RecipeBuilder } from "./RecipeBuilder";
import { useMaterialUnits } from "../hooks/useMaterialUnits";
import { SaveToLibraryModal } from "./SaveToLibraryModal";

const f = (v) => v == null || v === "" ? 0 : Number(v) || 0;

/* ============================================================
 * Read view — collapsed by default, expanded inline below the
 * line row when the user clicks the chevron. Shows the snapshot
 * recipe arrays as a read-only summary with override-flag dots
 * mirroring the cell-level overrides on the parent line.
 * ============================================================ */
export function RecipeReadView({ line, onEditRecipe, hasLibraryLink }) {
  // LT-18c §2 — legacy recipe lines may hold a unit KEY; render the label.
  const { labelFor: unitLabelFor } = useMaterialUnits();
  const arrays = useMemo(() => synthesiseSnapshotArrays(line.recipe_snapshot, line) || {
    recipe_labour: [], recipe_materials: [], recipe_plant: [], recipe_subby: []
  }, [line]);
  const totals = useMemo(() => computeRecipeTotals({ ...arrays, wastage_pct_override: line.wastage_pct_override ?? null }), [arrays, line.wastage_pct_override]);
  const flat = arrays.flat_material && arrays.flat_material.rate != null ? arrays.flat_material : null;   // LT-36
  const empty =
    !(arrays.recipe_labour || []).length &&
    !(arrays.recipe_materials || []).length &&
    !(arrays.recipe_plant || []).length &&
    !(arrays.recipe_subby || []).length &&
    !flat;

  const total = (totals.recipe_labour_total || 0) + (totals.recipe_materials_total || 0) + (totals.recipe_plant_total || 0);

  /** Override-flag indicators — mirror the cell-level flags on the parent line. */
  const flagDot = (active) =>
    active ? (
      <span
        className="inline-block rounded-full ml-1.5"
        style={{ width: 6, height: 6, background: "#D97706", verticalAlign: "middle" }}
        title="Manually overridden in this tender"
      />
    ) : null;

  return (
    <div className="px-7 pt-3 pb-4 bg-bg" data-testid={`recipe-read-view-${line.id}`}>
      <div className="rounded-lg border border-line bg-surface" style={{ maxWidth: 980 }}>
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-line-soft bg-surface-2/40">
          <div className="flex items-center gap-2">
            <Sparkles size={12} className="text-accent" />
            <span className="col-head text-ink-2" style={{ fontWeight: 700 }}>
              Recipe {hasLibraryLink ? "(linked to library)" : "(per-tender only)"}
            </span>
            {!hasLibraryLink && (
              <span className="text-ink-4" style={{ fontSize: 10.5 }}>
                · ad-hoc line, no master library item
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onEditRecipe}
            data-testid={`recipe-edit-btn-${line.id}`}
            className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md bg-accent text-white hover:bg-accent-deep transition-colors"
            style={{ fontSize: 11.5, fontWeight: 500 }}
          >
            <Edit3 size={11} /> Edit recipe
          </button>
        </div>

        {empty ? (
          <div className="px-4 py-5 text-center">
            <p className="text-ink-3" style={{ fontSize: 12 }}>
              No itemised recipe yet for this line.
            </p>
            <p className="text-ink-4 mt-0.5" style={{ fontSize: 11.5 }}>
              Click <span className="text-accent" style={{ fontWeight: 500 }}>Edit recipe</span> to break down labour, materials and plant per unit.
            </p>
          </div>
        ) : (
          <div className="px-4 py-3">
            {/* Labour */}
            {(arrays.recipe_labour || []).length > 0 && (
              <ReadSection title="Labour" total={totals.recipe_labour_total}>
                <ReadTable>
                  <ReadHead cols={["Trade", "Hours", "£/hr", "Total"]} />
                  {arrays.recipe_labour.map((e, i) => (
                    <ReadRow key={e.id || i} cells={[
                      e.trade_name || "—",
                      <span key="h">
                        {f(e.hours_per_unit).toFixed(2)}
                        {i === 0 ? flagDot(line.hours_override) : null}
                      </span>,
                      <span key="r">
                        {gbp(f(e.hourly_rate))}
                        {i === 0 ? flagDot(line.hourly_rate_override) : null}
                      </span>,
                      gbp(f(e.hours_per_unit) * f(e.hourly_rate))
                    ]} />
                  ))}
                </ReadTable>
              </ReadSection>
            )}

            {/* Materials — LT-36 flat figure */}
            {flat && (
              <ReadSection title="Materials (flat rate)" total={totals.recipe_materials_total} flag={flagDot(line.material_rate_override)}>
                <p className="text-ink-3 px-1.5 py-1" style={{ fontSize: 11.5 }} data-testid={`recipe-read-flat-${line.id}`}>
                  {gbp(f(flat.rate))} per {line.unit || "unit"} as one figure — nothing itemised to order{flat.note ? ` (${flat.note})` : ""}.
                </p>
              </ReadSection>
            )}
            {/* Materials */}
            {!flat && (arrays.recipe_materials || []).length > 0 && (
              <ReadSection title="Materials" total={totals.recipe_materials_total} flag={flagDot(line.material_rate_override)}>
                <ReadTable>
                  <ReadHead cols={["Description", "Qty", "Unit", "Wastage", "Rate", "Total"]} />
                  {arrays.recipe_materials.map((e, i) => {
                    const w = resolveEntryWastage(e, line.wastage_pct_override);
                    const cost = entryCostPerUnit(e, line.wastage_pct_override);
                    return (
                    <ReadRow key={e.id || i} cells={[
                      e.description || "—",
                      f(e.qty_per_unit).toFixed(2) + (e.coverage_basis === "coverage_unit" ? " (coverage)" : ""),
                      unitLabelFor(e.unit) || "—",
                      <span key="w" title={WASTAGE_SOURCE_LABEL[w.source]}>{w.pct}% <span className="text-ink-4">· {w.source === "line" ? "line" : w.source === "recipe" ? "recipe" : w.source === "material" ? "material" : "0%"}</span></span>,
                      gbp(f(e.rate)),
                      cost == null
                        ? <span key="c" className="text-note-red" style={{ fontWeight: 600 }}>coverage missing</span>
                        : gbp(cost)
                    ]} />
                    );
                  })}
                </ReadTable>
              </ReadSection>
            )}

            {/* Plant */}
            {(arrays.recipe_plant || []).length > 0 && (
              <ReadSection title="Plant" total={totals.recipe_plant_total} flag={flagDot(line.plant_rate_override)}>
                <ReadTable>
                  <ReadHead cols={["Description", "Total"]} />
                  {arrays.recipe_plant.map((e, i) => (
                    <ReadRow key={e.id || i} cells={[
                      e.description || "—",
                      gbp(f(e.cost_per_unit))
                    ]} />
                  ))}
                </ReadTable>
              </ReadSection>
            )}

            {/* Subby */}
            {(arrays.recipe_subby || []).length > 0 && (
              <ReadSection title="Subby" total={totals.recipe_subby_total} flag={flagDot(line.subby_rate_override)}>
                <ReadTable>
                  <ReadHead cols={["Description", "Total"]} />
                  {arrays.recipe_subby.map((e, i) => (
                    <ReadRow key={e.id || i} cells={[
                      e.description || "—",
                      gbp(f(e.cost_per_unit))
                    ]} />
                  ))}
                </ReadTable>
              </ReadSection>
            )}

            {/* Per-unit total footer */}
            <div className="flex items-center justify-end gap-2.5 mt-2 pt-2.5 border-t border-line-soft">
              <span className="col-head text-ink-3" style={{ fontSize: 9.5 }}>Per-unit total{line.unit ? ` (per ${line.unit})` : ""}</span>
              <span className="font-mono tnum text-ink" style={{ fontSize: 14, fontWeight: 700 }}>{gbp(total)}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ReadSection({ title, total, flag, children }) {
  return (
    <div className="mb-2.5">
      <div className="flex items-baseline justify-between mb-1">
        <span className="col-head text-ink-2" style={{ fontWeight: 700 }}>{title}</span>
        <span className="font-mono tnum text-ink" style={{ fontSize: 12.5, fontWeight: 600 }}>
          {gbp(total)}{flag}
        </span>
      </div>
      {children}
    </div>
  );
}

function ReadTable({ children }) {
  return <table className="w-full" style={{ borderCollapse: "separate", borderSpacing: 0, fontSize: 12 }}>{children}</table>;
}
function ReadHead({ cols }) {
  return (
    <thead>
      <tr>
        {cols.map((c, i) => (
          <th key={i} className="col-head text-ink-3" style={{
            padding: "3px 6px",
            textAlign: i === 0 ? "left" : "right",
            fontSize: 9.5,
            borderBottom: "1px solid #EDEAE2"
          }}>{c}</th>
        ))}
      </tr>
    </thead>
  );
}
function ReadRow({ cells }) {
  return (
    <tr>
      {cells.map((c, i) => (
        <td key={i} className={i === 0 ? "text-ink-2" : "text-ink font-mono tnum"} style={{
          padding: "4px 6px",
          textAlign: i === 0 ? "left" : "right",
          fontSize: 12,
          borderBottom: "1px dashed #F0EEE6"
        }}>{c}</td>
      ))}
    </tr>
  );
}

/* ============================================================
 * RecipeDrawer — slide-in from right (560px). Edits the recipe
 * arrays on the line's snapshot only. On save, the drawer:
 *   1) writes the new arrays onto line.recipe_snapshot
 *   2) recomputes line cells (hours, rates, additional_trades)
 *   3) clears all override flags (line is freshly synced to recipe)
 *   4) calls the parent onSave(line) to persist via tender autosave
 *
 * "Push changes back to library" calls
 * POST /api/library/{library_item_id}/push-from-tender with the
 * current recipe arrays. Available only for library-derived lines.
 * ============================================================ */
export function RecipeDrawer({
  open, onClose, line, tenderId,
  labourRates = [], materials = [], masterPhases = [],
  onSave, onApplyToLine,
}) {
  const [arrays, setArrays] = useState({
    recipe_labour: [], recipe_materials: [], recipe_plant: [], recipe_subby: []
  });
  const [pushing, setPushing] = useState(false);
  const [confirmPush, setConfirmPush] = useState(false);
  // Section 4 (brought forward) — modal for promoting an ad-hoc line into a
  // brand-new master library item using the live drawer recipe state.
  const [saveToLibOpen, setSaveToLibOpen] = useState(false);

  // Reset drawer state every time it opens for a different line
  useEffect(() => {
    if (!open || !line) return;
    const seed = synthesiseSnapshotArrays(line.recipe_snapshot, line) || {};
    setArrays({
      recipe_labour:    [...(seed.recipe_labour    || [])],
      recipe_materials: [...(seed.recipe_materials || [])],
      recipe_plant:     [...(seed.recipe_plant     || [])],
      recipe_subby:     [...(seed.recipe_subby     || [])],
      flat_material:    seed.flat_material ? { ...seed.flat_material } : null,   // LT-36
    });
    setConfirmPush(false);
    setSaveToLibOpen(false);
  }, [open, line]);

  const totals = useMemo(() => computeRecipeTotals({ ...arrays, wastage_pct_override: line?.wastage_pct_override ?? null }), [arrays, line]);
  const hasLibrary = !!(line && line.recipe_snapshot && line.recipe_snapshot.library_item_id);
  // For lines NOT linked to a library item, expose the "Save to library" path
  // (creates a NEW master). Linked lines instead use "Push to library" which
  // updates their existing master.
  const canSaveAsNewLibrary = !!line && !hasLibrary;

  const performSave = () => {
    const nextLine = applyRecipeArraysToLine(line, arrays);
    onSave?.(nextLine);
    toast.success("Recipe updated for this line");
    onClose?.();
  };

  const performPush = async () => {
    if (!hasLibrary) return;
    setPushing(true);
    try {
      await api.post(`/library/${line.recipe_snapshot.library_item_id}/push-from-tender`, {
        recipe_labour:    arrays.recipe_labour    || [],
        recipe_materials: arrays.recipe_materials || [],
        recipe_plant:     arrays.recipe_plant     || [],
        recipe_subby:     arrays.recipe_subby     || [],
        flat_material:    arrays.flat_material || null,   // LT-36
        source_tender_id: tenderId || null,
        source_line_id:   line.id || null,
        source_line_description: line.description || null,
      });
      toast.success("Pushed to master library");
      setConfirmPush(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Push failed");
    } finally {
      setPushing(false);
    }
  };

  if (!open) return null;
  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[55] bg-ink/30 transition-opacity"
        onMouseDown={onClose}
        data-testid="recipe-drawer-backdrop"
      />
      {/* Drawer */}
      <aside
        className="fixed top-0 right-0 bottom-0 z-[56] bg-bg border-l border-line shadow-card flex flex-col"
        style={{ width: 560 }}
        data-testid="recipe-drawer"
      >
        {/* Header */}
        <header className="flex items-center justify-between px-5 py-3.5 border-b border-line bg-surface">
          <div className="min-w-0">
            <p className="col-head text-ink-3" style={{ fontSize: 9.5 }}>
              Recipe · this tender only
            </p>
            <h2 className="font-display text-ink truncate" style={{ fontSize: 15, fontWeight: 600 }}>
              {line?.description || "Untitled line"}
            </h2>
            <p className="text-ink-3 mt-0.5" style={{ fontSize: 11 }}>
              Edits affect this tender's line snapshot only — master library item is untouched.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {hasLibrary && (
              <QuietButton
                onClick={() => setConfirmPush(true)}
                icon={pushing ? Loader2 : Upload}
                disabled={pushing}
                data-testid="recipe-push-to-library"
              >
                Push to library
              </QuietButton>
            )}
            <button
              type="button"
              onClick={onClose}
              data-testid="recipe-drawer-close"
              className="text-ink-3 hover:text-ink p-1.5 rounded-md hover:bg-surface-2"
              aria-label="Close drawer"
            >
              <X size={15} />
            </button>
          </div>
        </header>

        {/* Body */}
        <div className="flex-1 overflow-auto px-5 py-5"
          onKeyDown={(e) => { if (e.key === "Enter" && e.target.tagName !== "TEXTAREA") e.preventDefault(); }}
        >
          <RecipeBuilder
            arrays={arrays}
            onChange={setArrays}
            totals={totals}
            unit={line?.unit}
            labourRates={labourRates}
            materials={materials}
            lineWastageOverride={line?.wastage_pct_override ?? null}
            showSubby
          />
        </div>

        {/* Footer */}
        <footer className="px-5 py-3.5 border-t border-line bg-surface flex items-center justify-between gap-2">
          {canSaveAsNewLibrary ? (
            <button
              type="button"
              onClick={() => setSaveToLibOpen(true)}
              data-testid="recipe-save-to-library"
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-accent hover:text-white hover:bg-accent transition-colors"
              style={{ fontSize: 12, fontWeight: 500, border: "1px solid #D6E2D8" }}
              title="Promote this line into a new master library item"
            >
              <BookPlus size={13} /> Save to library
            </button>
          ) : <span />}
          <div className="flex items-center gap-2">
            <QuietButton onClick={onClose} data-testid="recipe-drawer-cancel">Cancel</QuietButton>
            <PrimaryButton onClick={performSave} icon={Save} data-testid="recipe-drawer-save">
              Save recipe to this line
            </PrimaryButton>
          </div>
        </footer>
      </aside>

      {/* Save-to-library modal — renders the new-library-item form pre-filled
          from the drawer's current recipe state (not the line's saved snapshot). */}
      <SaveToLibraryModal
        open={saveToLibOpen}
        onClose={() => setSaveToLibOpen(false)}
        sourceLine={line}
        sourceArrays={arrays}
        tenderId={tenderId}
        masterPhases={masterPhases}
        labourRates={labourRates}
        materials={materials}
        onCreated={() => { setSaveToLibOpen(false); }}
        onApplyToLine={onApplyToLine}
      />

      {/* Push-to-library confirm */}
      <Modal
        open={confirmPush}
        onClose={() => setConfirmPush(false)}
        title="Push recipe to master library?"
        testid="push-library-modal"
        footer={
          <>
            <QuietButton onClick={() => setConfirmPush(false)} data-testid="push-cancel">
              Cancel
            </QuietButton>
            <PrimaryButton
              onClick={performPush}
              disabled={pushing}
              icon={pushing ? Loader2 : Upload}
              data-testid="push-confirm"
            >
              Push to library
            </PrimaryButton>
          </>
        }
      >
        <p className="text-ink-2 mb-2" style={{ fontSize: 13 }}>
          Update the master library item <span className="font-mono tnum text-ink">
            {line?.recipe_snapshot?.library_item_description || "—"}
          </span> with the recipe currently shown in this drawer?
        </p>
        <p className="text-ink-3" style={{ fontSize: 12 }}>
          Other tenders that have already added this library item retain their current snapshot —
          only future "Add from library" actions will pick up the new recipe. The change is recorded in the audit log.
        </p>
      </Modal>
    </>
  );
}
